import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { workSessionCloseReasonLabel, type WorkSessionCloseReason } from "@/lib/workSessions";

export type AutoCheckoutAlert = {
  id: string;
  userId: string;
  workerName: string;
  locationId: string;
  locationAddress: string;
  endedAt: string;
  closeReason: WorkSessionCloseReason;
  closeReasonLabel: string;
  distanceMeters: number | null;
};

export function useRecentAutoCheckouts(orgId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["cleaning-auto-checkouts", orgId ?? ""],
    enabled: Boolean(orgId) && enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<AutoCheckoutAlert[]> => {
      const since = new Date();
      since.setDate(since.getDate() - 7);
      const { data, error } = await supabase
        .from("cleaning_work_sessions")
        .select("id, user_id, location_id, ended_at, close_reason, check_out_distance_meters, cleaning_locations(address)")
        .eq("org_id", orgId!)
        .eq("status", "auto_closed")
        .gte("ended_at", since.toISOString())
        .order("ended_at", { ascending: false })
        .limit(20);

      if (error) throw error;

      const userIds = [...new Set((data ?? []).map((row) => row.user_id))];
      const names = new Map<string, string>();
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, full_name")
          .in("id", userIds);
        for (const profile of profiles ?? []) {
          names.set(profile.id, profile.full_name || "Pracownik");
        }
      }

      return (data ?? []).map((row) => {
        const loc = Array.isArray(row.cleaning_locations) ? row.cleaning_locations[0] : row.cleaning_locations;
        const closeReason = (row.close_reason ?? "geofence") as WorkSessionCloseReason;
        return {
          id: row.id,
          userId: row.user_id,
          workerName: names.get(row.user_id) || "Pracownik",
          locationId: row.location_id,
          locationAddress: loc?.address || "Brak adresu",
          endedAt: row.ended_at ?? "",
          closeReason,
          closeReasonLabel: workSessionCloseReasonLabel(closeReason),
          distanceMeters: row.check_out_distance_meters,
        };
      });
    },
  });
}
